// Enriched editorial content for France's largest cities, where VSP search
// competition is highest. Adds unique, genuinely useful local content
// (districts, low-emission zones, practical tips) + extra FAQ for stronger SEO.
// Dependency-free (no imports) so the prerender script can load it too.
// Keyed by the city slug used in lib/cities.js.

export const CITY_EXTRAS = {
  paris: {
    lead:
      "Paris concentre un trafic dense et de nombreuses voies interdites aux voiturettes. La voiture sans permis y est pratique pour les courts trajets intra-muros, à condition d'éviter le Boulevard Périphérique et les voies sur berges rapides. vspo calcule des itinéraires qui restent sur les boulevards et rues autorisés.",
    zones: ["Le Marais", "Montmartre", "Belleville", "Les Batignolles", "Bercy", "Butte-aux-Cailles"],
    tips: [
      "Zone à faibles émissions (ZFE) du Grand Paris : vérifiez la vignette Crit'Air de votre VSP, surtout pour les modèles thermiques.",
      "Privilégiez le stationnement résidentiel et les parkings ; les places se libèrent plus facilement en dehors des heures de pointe.",
      "Pour les VSP électriques, Paris dispose d'un réseau dense de bornes publiques ; filtrez par prise compatible dans vspo.",
    ],
    extraFaq: [
      {
        q: "Une voiture sans permis peut-elle rouler dans Paris malgré la ZFE ?",
        a: "Oui, mais la zone à faibles émissions du Grand Paris s'applique aussi aux VSP thermiques selon leur vignette Crit'Air. Les VSP électriques ne sont pas concernées par les restrictions. Vérifiez votre catégorie avant de circuler.",
      },
      {
        q: "Peut-on prendre le périphérique parisien en voiture sans permis ?",
        a: "Non. Le Boulevard Périphérique est une voie rapide interdite aux voitures sans permis. vspo trace un itinéraire par les boulevards et avenues autorisés à l'intérieur de Paris.",
      },
    ],
  },
  lyon: {
    lead:
      "À Lyon, les voiturettes circulent bien dans la Presqu'île et les arrondissements centraux, mais le périphérique Laurent-Bonnevay (M6/M7) et plusieurs tunnels leur sont interdits. vspo évite ces axes et compose avec le relief de la Croix-Rousse.",
    zones: ["Presqu'île", "Croix-Rousse", "Vieux Lyon", "Confluence", "Part-Dieu", "Guillotière"],
    tips: [
      "ZFE de la Métropole de Lyon : contrôlez la vignette Crit'Air pour une VSP thermique.",
      "La Croix-Rousse est pentue : anticipez l'autonomie d'une VSP électrique dans les montées.",
      "Évitez les tunnels (Croix-Rousse, Fourvière) réservés au trafic rapide ; vspo passe par la surface.",
    ],
    extraFaq: [
      {
        q: "Les tunnels de Lyon sont-ils autorisés aux voitures sans permis ?",
        a: "Non, les grands tunnels urbains de Lyon sont généralement interdits aux VSP. vspo privilégie les itinéraires de surface, autorisés et adaptés à une vitesse de 45 km/h.",
      },
      {
        q: "Peut-on rouler en VSP sur le périphérique de Lyon ?",
        a: "Non. Le boulevard périphérique Laurent-Bonnevay (M6/M7) est une voie rapide interdite aux voiturettes. vspo calcule un trajet par le réseau ordinaire.",
      },
    ],
  },
  marseille: {
    lead:
      "Marseille est une ville étendue et vallonnée. En voiture sans permis, on circule facilement autour du Vieux-Port et dans les quartiers centraux, mais la rocade L2 (A507) et l'autoroute du Littoral (A55) sont interdites. vspo trace un itinéraire par les boulevards urbains.",
    zones: ["Vieux-Port", "Le Panier", "La Joliette", "Castellane", "La Plaine", "Vauban"],
    tips: [
      "Les ruelles du centre sont étroites et pentues : une VSP s'y faufile bien mais surveillez le stationnement.",
      "Évitez la L2 et l'A55, voies rapides interdites aux voiturettes.",
      "Pour une VSP électrique, anticipez la recharge avant les secteurs en hauteur (collines).",
    ],
    extraFaq: [
      {
        q: "La rocade L2 est-elle autorisée en voiture sans permis à Marseille ?",
        a: "Non. La rocade L2 (A507) est une voie rapide interdite aux VSP. vspo reste sur les boulevards et avenues du réseau ordinaire marseillais.",
      },
    ],
  },
  lille: {
    lead:
      "À Lille, la voiture sans permis est idéale pour relier le Vieux-Lille, Wazemmes et les communes proches, en évitant le périphérique (N356) et les voies rapides. vspo compose aussi avec la ZFE de la métropole européenne de Lille.",
    zones: ["Vieux-Lille", "Wazemmes", "Vauban-Esquermes", "Moulins", "Fives"],
    tips: [
      "ZFE de la MEL : vérifiez la vignette Crit'Air de votre VSP thermique.",
      "Attention aux pavés du Vieux-Lille, inconfortables mais praticables en voiturette.",
      "Les communes limitrophes (Roubaix, Tourcoing) se rejoignent par le réseau ordinaire, sans voie rapide.",
    ],
    extraFaq: [
      {
        q: "Peut-on circuler en VSP dans la métropole de Lille malgré la ZFE ?",
        a: "Oui pour les VSP électriques ; pour les VSP thermiques, la zone à faibles émissions de la MEL s'applique selon la vignette Crit'Air. Vérifiez votre catégorie avant de rouler.",
      },
    ],
  },
  nice: {
    lead:
      "Nice, entre mer et collines, se parcourt très bien en voiture sans permis dans le Vieux-Nice et le long des grands boulevards. La Voie Mathis (M6202) et les voies rapides restent interdites : vspo les contourne automatiquement.",
    zones: ["Vieux-Nice", "Cimiez", "Libération", "Le Port", "Riquier"],
    tips: [
      "Les collines (Cimiez) sollicitent l'autonomie d'une VSP électrique : rechargez avant de monter.",
      "Évitez la Voie Mathis, voie rapide interdite aux voiturettes.",
      "Le stationnement est tendu en bord de mer : visez les parkings et les horaires creux.",
    ],
    extraFaq: [
      {
        q: "La Voie Mathis est-elle interdite aux voitures sans permis à Nice ?",
        a: "Oui. La Voie Mathis (M6202) est une voie rapide interdite aux VSP. vspo calcule un itinéraire par les boulevards et avenues autorisés de Nice.",
      },
    ],
  },
  strasbourg: {
    lead:
      "Strasbourg, ville cyclable et apaisée, est agréable en voiture sans permis autour de la Grande-Île et dans les quartiers comme la Krutenau ou Neudorf. L'A35 (voie rapide urbaine) est interdite : vspo l'évite systématiquement.",
    zones: ["Grande-Île", "Krutenau", "Neudorf", "Petite France", "Esplanade"],
    tips: [
      "Partagez prudemment la voirie avec les nombreux cyclistes et le tram.",
      "Évitez l'A35, voie rapide urbaine interdite aux VSP.",
      "Le centre historique est en partie piéton : vspo contourne les secteurs fermés à la circulation.",
    ],
    extraFaq: [
      {
        q: "Peut-on prendre l'A35 en voiture sans permis à Strasbourg ?",
        a: "Non. L'A35, voie rapide urbaine, est interdite aux voiturettes. vspo reste sur le réseau ordinaire adapté à une conduite à 45 km/h.",
      },
    ],
  },
  brest: {
    lead:
      "À Brest, la voiture sans permis relie facilement le centre (rue de Siam), Recouvrance et les quartiers proches. Les voies express N165/N12 sont interdites : vspo trace un trajet urbain adapté au relief et au vent de la pointe bretonne.",
    zones: ["Siam", "Recouvrance", "Saint-Marc", "Lambézellec"],
    tips: [
      "Le relief brestois et le vent peuvent réduire l'autonomie d'une VSP électrique.",
      "Évitez les voies express N165 et N12, interdites aux voiturettes.",
      "Le pont de Recouvrance est accessible : vspo l'intègre si le trajet le justifie.",
    ],
    extraFaq: [
      {
        q: "Les voies express N165 et N12 sont-elles interdites aux VSP à Brest ?",
        a: "Oui. Ces voies express sont interdites aux voitures sans permis. vspo calcule un itinéraire par les rues et boulevards autorisés de Brest.",
      },
    ],
  },
  rennes: {
    lead:
      "Rennes se prête bien à la voiture sans permis dans son centre historique et les quartiers comme le Thabor ou Villejean. La rocade (N136) est interdite aux voiturettes : vspo l'évite et reste sur les axes urbains.",
    zones: ["Centre historique", "Thabor-Saint-Hélier", "Villejean", "Sud-Gare", "Bourg-l'Évêque"],
    tips: [
      "ZFE de Rennes Métropole : contrôlez la vignette Crit'Air d'une VSP thermique.",
      "Évitez la rocade N136, voie rapide interdite aux VSP.",
      "Le centre compte des rues piétonnes : vspo les contourne automatiquement.",
    ],
    extraFaq: [
      {
        q: "Peut-on rouler sur la rocade de Rennes en voiture sans permis ?",
        a: "Non. La rocade de Rennes (N136) est une voie rapide interdite aux voiturettes. vspo trace un itinéraire par le réseau ordinaire intra-rocade.",
      },
    ],
  },
  nantes: {
    lead:
      "À Nantes, la voiture sans permis circule bien du centre à l'Île de Nantes et vers Chantenay, en évitant le périphérique nantais. vspo compose avec les nombreux ponts et la ZFE de la métropole.",
    zones: ["Centre-ville", "Île de Nantes", "Chantenay", "Doulon", "Hauts-Pavés"],
    tips: [
      "ZFE de Nantes Métropole : vérifiez la vignette Crit'Air de votre VSP thermique.",
      "Évitez le périphérique nantais, voie rapide interdite aux voiturettes.",
      "Les ponts sur la Loire sont autorisés : vspo les emprunte quand c'est pertinent.",
    ],
    extraFaq: [
      {
        q: "Le périphérique nantais est-il autorisé aux voitures sans permis ?",
        a: "Non. Le périphérique de Nantes est une voie rapide interdite aux VSP. vspo calcule un trajet par les boulevards et ponts autorisés de la ville.",
      },
    ],
  },
};

export function cityExtras(slug) {
  return CITY_EXTRAS[slug] || null;
}
